import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faBuilding, faCodeBranch, faAddressBook,
  faIndustry, faHandshake, faFileAlt, faHandHoldingUsd,
} from "@fortawesome/free-solid-svg-icons";
import { Alert, Button, Card, Col, Row, Spinner } from "@themesberg/react-bootstrap";
import { Link } from "react-router-dom";
import { API_URL } from "../../const";
import { Routes } from "../../routes";
import { readAuthSession } from "../../utils/authSession";

const SUMMARY_CARDS = [
  { key: "vendors", label: "Total Vendors", icon: faUsers },
  { key: "companies", label: "Total Companies", icon: faBuilding },
  { key: "branches", label: "Total Branches", icon: faCodeBranch },
  { key: "customers", label: "Total Customers", icon: faAddressBook },
  { key: "industries", label: "Total Industries", icon: faIndustry },
  { key: "principals", label: "Total Principals", icon: faHandshake },
  { key: "quotes", label: "Total Quotes", icon: faFileAlt },
  { key: "po_sales", label: "Total PO Sales", icon: faHandHoldingUsd },
];

export default function SuperAdminDashboard() {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const { token } = readAuthSession();
    axios.get(API_URL + "dashboard/counts", {
      headers: { Accept: "application/json", Authorization: "Bearer " + token },
      timeout: 15000,
    }).then(({ data }) => {
      if (!data || data.status === false || !SUMMARY_CARDS.every(({ key }) =>
        Number.isInteger(data[key]) && data[key] >= 0)) {
        throw new Error("Invalid dashboard counts");
      }
      if (active) setCounts(data);
    }).catch(() => {
      if (active) setError("Unable to load dashboard totals. Please try again.");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [attempt]);

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <p className="dashboard-eyebrow mb-1">Overview</p>
          <h2 className="dashboard-title mb-1">Super Admin Dashboard</h2>
          <p className="dashboard-subtitle mb-0">An overview of all vendor accounts and their records.</p>
        </div>
        <Button as={Link} to={Routes.Admin.path} variant="primary" className="btn-quote-blue dashboard-cta">
          <FontAwesomeIcon icon={faUsers} className="me-2" />
          Manage Vendors
        </Button>
      </div>
      {loading ? (
        <div className="dashboard-empty-state" role="status">
          <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
          Loading dashboard...
        </div>
      ) : error ? (
        <Alert variant="danger" className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <span>{error}</span>
          <Button variant="outline-danger" onClick={() => setAttempt(value => value + 1)}>Retry</Button>
        </Alert>
      ) : (
        <Row className="g-4 mb-4">
          {SUMMARY_CARDS.map(({ key, label, icon }, index) => (
            <Col key={key} xs={12} sm={6} xl={3}>
              <Card className={"dashboard-stat-card h-100 dashboard-stat-card--" + (index === 0 ? "primary" : "neutral")}>
                <Card.Body>
                  <div className="dashboard-stat-icon"><FontAwesomeIcon icon={icon} /></div>
                  <p className="dashboard-stat-label">{label}</p>
                  <h3 className="dashboard-stat-value">{counts[key].toLocaleString("en-IN")}</h3>
                  <p className="dashboard-stat-meta mb-0">{key === "vendors" ? "Vendor accounts" : "Across all vendors"}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}
